import { useNuxtApp, useRuntimeConfig } from "#app"; // 导入 Nuxt 运行时能力
import {
  getPrefixUrl,
  getMockJwtByPrefixUrl,
  checkMallFetchUrl,
  getCookieToken,
  getCurrentServer,
  getOpenAccessTokenKey,
} from "@/utils/globalData"; // 导入 `useNuxtApp`
import { traceSsr } from "@/utils/ssrTrace";
import { getSiteMatchEndpointPath } from "@/utils/siteMatchEndpoint";
import { resolveSiteTemplate } from "@/templates/_core/getCurrentTemplate";
import { reportAddCartVisit, resolveOnlineVisitShopId } from "@/utils/onlineVisit";

import { useMainStore } from "../composables/store";

const MALL_GOODS_LIST_URL = "/mall/admin/mallGoods/querySpuItemList?isShop=1&uncheck401=1";
const MALL_MIXED_GOODS_LIST_URL = "/mall/admin/mallGoods/queryMixedItemList?isShop=1&uncheck401=1";
const MALL_GOODS_MENU_URL = "/mall/admin/mallGoods/menuList?isShop=1&uncheck401=1&getCache=1";

function normalizeShopContextValue(value) {
  return String(value || "").trim();
}

function isPlainRecord(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function readCookieValue(name, headers) {
  try {
    const cookieValue = useCookie(name, { path: "/" });
    if (cookieValue && cookieValue.value) return cookieValue.value;
  } catch (e) {
    // Fallback to request headers below when Nuxt cookie context is unavailable.
  }

  if (headers && headers.cookie && typeof headers.cookie === "string") {
    const match = headers.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (_) {
        return match[1];
      }
    }
  }
  return "";
}

function persistDealerShopIdCookie(value) {
  const normalizedDealerShopId = normalizeShopContextValue(value);
  if (!normalizedDealerShopId) {
    return;
  }

  try {
    const dealerShopIdCookie = useCookie("dealerShopId", { path: "/" });
    if (normalizeShopContextValue(dealerShopIdCookie.value) !== normalizedDealerShopId) {
      dealerShopIdCookie.value = normalizedDealerShopId;
    }
  } catch (error) {
    // Ignore cookie sync errors outside of an active Nuxt context.
  }
}

function resolveCurrentUserId(params = {}) {
  const bodyParam = params.param || {};
  if (bodyParam.userId) return bodyParam.userId;
  if (params.userId) return params.userId;

  try {
    const store = useMainStore();
    return store?.$state?.mallUserInfo?.id || store?.$state?.userInfo?.id || "";
  } catch (e) {
    return "";
  }
}

function resolveCurrentDealerShopId(params = {}, headers) {
  const bodyParam = isPlainRecord(params.param) ? params.param : {};
  const explicitDealerShopId = normalizeShopContextValue(
    bodyParam.dealerShopId || params.dealerShopId
  );
  if (explicitDealerShopId) return explicitDealerShopId;

  const explicitShopId = normalizeShopContextValue(bodyParam.shopId || params.shopId);
  if (explicitShopId) return explicitShopId;

  const cookieDealerShopId = normalizeShopContextValue(
    readCookieValue("dealerShopId", headers)
  );
  if (cookieDealerShopId) return cookieDealerShopId;

  try {
    const store = useMainStore();
    const hostSite = getCurrentServer(headers, getCurrentRequestUrl());
    const tokenKey = getOpenAccessTokenKey(hostSite);
    const candidateList = [
      typeof store?.getDealerShopId === "function" ? store.getDealerShopId(hostSite) : "",
      typeof store?.getServerShopId === "function" ? store.getServerShopId(hostSite) : "",
      store?.$state?.dealerShopIdKeyMap?.[tokenKey] || "",
      typeof store?.getShopId === "function" ? store.getShopId(hostSite) : "",
      store?.$state?.shopIdKeyMap?.[tokenKey] || "",
    ];

    const resolvedDealerShopId = candidateList.find((item) =>
      normalizeShopContextValue(item)
    );
    return normalizeShopContextValue(resolvedDealerShopId);
  } catch (e) {
    return "";
  }
}

function shouldUseMixedGoodsList(headers) {
  try {
    const store = useMainStore();
    const currentServer = getCurrentServer(headers, getCurrentRequestUrl());
    const storeInfo = store.getStoreInfoDataMap(currentServer) || {};
    const storePageInfo = storeInfo.storePageInfo || {};
    const siteTemplate = resolveSiteTemplate(store.getSiteInfo(), {
      ...storeInfo,
      ...storePageInfo,
    });

    return siteTemplate.features?.supportsComboProducts === true;
  } catch (error) {
    return false;
  }
}

function normalizeShopRequestBody(body, headers) {
  if (!isPlainRecord(body)) {
    return body;
  }

  const resolvedDealerShopId = resolveCurrentDealerShopId(body, headers);
  if (!resolvedDealerShopId) {
    return body;
  }

  persistDealerShopIdCookie(resolvedDealerShopId);

  if (isPlainRecord(body.param)) {
    if (normalizeShopContextValue(body.param.dealerShopId)) {
      return body;
    }

    return {
      ...body,
      param: {
        ...body.param,
        dealerShopId: resolvedDealerShopId,
      },
    };
  }

  if (!("param" in body) && !normalizeShopContextValue(body.dealerShopId)) {
    return {
      ...body,
      dealerShopId: resolvedDealerShopId,
    };
  }

  return body;
}

function normalizeMallGoodsRequestBody(params = {}, headers) {
  const nextBody = params && params.param
    ? { ...params, param: { ...params.param } }
    : { param: { ...(params || {}) } };
  const param = nextBody.param;

  if (!param.title && param.keyword) {
    param.title = param.keyword;
  }
  if (!param.spShopCategoryId && param.categoryId) {
    param.spShopCategoryId = param.categoryId;
  }
  if (!param.stockStatus && param.availability) {
    param.stockStatus = param.availability;
  }
  if (!param.sortType && param.sortBy) {
    param.sortType = param.sortBy;
  }

  delete param.keyword;
  delete param.categoryId;
  delete param.availability;
  delete param.sortBy;

  if (param.stockStatus === "0") {
    delete param.stockStatus;
  }
  if (param.sortType === "sales") {
    delete param.sortType;
  }

  const dealerShopId = resolveCurrentDealerShopId(nextBody, headers);
  const userId = resolveCurrentUserId(nextBody);
  if (dealerShopId) {
    param.dealerShopId = dealerShopId;
  }
  if (dealerShopId && userId) {
    param.userId = userId;
  } else {
    delete param.userId;
  }

  delete nextBody.dealerShopId;
  delete nextBody.userId;
  return nextBody;
}

function normalizeMatchUrlValue(currentServer = "") {
  const normalizedValue = String(currentServer || "").trim();
  if (!normalizedValue) {
    return "";
  }

  if (/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue;
  }

  return `http://${normalizedValue}`;
}

function getCurrentRequestUrl() {
  try {
    return useNuxtApp()?.ssrContext?.event?.node?.req?.url || "";
  } catch (error) {
    return "";
  }
}

function getUrlOrigin(url = "") {
  try {
    return new URL(url).origin;
  } catch (error) {
    return "";
  }
}

function hasRedirectSubDomainInRequest() {
  const requestUrl = process.client && typeof window !== "undefined"
    ? window.location.href
    : getCurrentRequestUrl();

  try {
    const url = new URL(requestUrl || "", "http://localhost");
    return !!String(url.searchParams.get("redirectSubDomain") || "").trim();
  } catch (error) {
    return false;
  }
}

function shouldTryDirectDevelopmentSiteInfo(response, normalizedCurrentServer = "") {
  if (process.env.NODE_ENV !== "development") {
    return false;
  }
  if (response?.success && response?.data) {
    return false;
  }
  const origin = getUrlOrigin(normalizedCurrentServer);
  if (!origin) {
    return false;
  }
  return !/(localhost|127\.0\.0\.1|192\.168\.|10\.|172\.(1[6-9]|2\d|3[0-1])\.)/i.test(origin);
}

function getDevelopmentCurrentDomainApiUrl(url = "", headers) {
  if (process.env.NODE_ENV !== "development" || !hasRedirectSubDomainInRequest()) {
    return "";
  }

  const currentServer = getCurrentServer(headers, getCurrentRequestUrl());
  const origin = getUrlOrigin(currentServer);
  if (!origin) {
    return "";
  }

  return `${origin}/api/cmsadmin${url}`;
}

async function postMallGoods(url, body, $fetch, headers) {
  const directUrl = getDevelopmentCurrentDomainApiUrl(url, headers);
  if (directUrl) {
    try {
      return await $fetch(directUrl, {
        method: "POST",
        body,
      });
    } catch (error) {
      traceSsr("mallGoods.directDevelopmentError", {
        directUrl,
        message: error?.message || String(error),
      });
    }
  }

  if ($fetch) {
    return makeUnSetupFetch($fetch, headers, url, {
      method: "POST",
      body,
    });
  }
  return useNuxtApp().$customFetch(url, {
    method: "POST",
    body,
  });
}



/**
 * 获取token
 
 * @param params
 * @returns {*}
 */
export function getOssToken(body, $fetch, headers) {
  
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/merchant/basic/aliyun/getToken?isShop=1",
      {
     
      }
    );
  }
  return useNuxtApp().$customFetch(
    "/merchant/basic/aliyun/getToken?isShop=1",
    
  );
}
export function makeUnSetupFetch($fetch, headers, url, options = {}) {
  const hostSite = getCurrentServer(headers, getCurrentRequestUrl());
  const tokenKey = getOpenAccessTokenKey(hostSite);
  const prefixUrl = getPrefixUrl(url, headers);
  const isShopUrl = url.indexOf("isShop=1") >= 0;
  const normalizedBody = isShopUrl
    ? normalizeShopRequestBody(options?.body, headers)
    : options?.body;
  const defaultHeaders = {
    ...(options.headers || {}),
  };
  const store = useMainStore();
  // 如果有 mock JWT 或 store 中的 token，将其添加到请求头
  const mockJwt = getMockJwtByPrefixUrl(prefixUrl);
  if (mockJwt) {
    defaultHeaders["openaccessjwt"] = mockJwt;
  }


  if (store && store.$state.tokenKeyMap[tokenKey]) {
    defaultHeaders["openaccessjwt"] = store.$state.tokenKeyMap[tokenKey];
  }

  if (store && store.$state.shopIdKeyMap[tokenKey]) {
    defaultHeaders["shopId"] = store.$state.shopIdKeyMap[tokenKey];
  }

  if (!defaultHeaders.shopId && !defaultHeaders.shopid) {
    const resolvedShopId = normalizeShopContextValue(
      (typeof store?.getShopId === "function" ? store.getShopId(hostSite) : "")
        || resolveCurrentDealerShopId(normalizedBody || {}, headers)
    );
    if (resolvedShopId) {
      defaultHeaders["shopId"] = resolvedShopId;
    }
  }

  let setOptions = {
    ...options,
    baseURL: prefixUrl,
    body: normalizedBody,
    headers: { ...(options.headers || {}), ...defaultHeaders },
  };

  if (process.client && (!options.body || !options.body.unhaveToken)) {
    let isMallUrl = checkMallFetchUrl(url);
    if (isMallUrl) {
      setOptions.headers.authorization = getMallAppToken();
    } else {
      const token = getToken();
      if (typeof setOptions.headers != "undefined") {
        if (typeof token === "string") {
          setOptions.headers.authorization = token;
        } else {
          setOptions.headers.authorization = "";
        }
      }
    }
  } else if (process.server && (!options.body || !options.body.unhaveToken)) {
    // 服务端时从 cookie 获取 sj_mall_admin_token
    const cookieToken = getCookieToken(headers);
    if (cookieToken) {
      setOptions.headers.authorization = cookieToken;
    }
  }

  return $fetch(url, setOptions);
}

/**
 * 获取轮播图信息
 
 * @param params
 * @returns {*}
 */
export function getBanner(params) {
  return useNuxtApp().$customFetch("/api/indexpic/list", {
    method: "GET",
    params,
  });
}

/**
 * 获取栏目
 
 * @param params
 * @returns {*}
 */
export function getColumn(params) {
  return useNuxtApp().$customFetch("/api/column/list", {
    method: "GET",
    params,
  });
}

/**
 * 获取某个资讯详情
 
 * @param params
 * @returns {*}
 */
export function getContentInfo(params) {
  return useNuxtApp().$customFetch("/api/content/info/" + params.id, {
    params,
  });
}

/**
 * 获取文章列表
 
 * @param params
 * @returns {*}
 */
export function getContentList(params) {
  return useNuxtApp().$customFetch("/api/content/list", {
    params: { ...params, limit: params.pageSize },
  });
}

/**
 * 分页获取友情链接
 
 * @param params
 * @returns {*}
 */
export function getConvenientList(params) {
  return useNuxtApp().$customFetch("/api/convenient/list", { params });
}

/**
 * 查询考生是否被录取
 
 * @param params
 * @returns {*}
 */
export function matriculatestat(params) {
  return useNuxtApp().$customFetch("/api/matriculate/stu/matriculatestate", {
    method: "POST",
    params,
  });
}

/**
 * 查询活动页
 
 * @param params
 * @returns {*}
 */
export function getActiveDetail(body = {}, $fetch, headers) {
  const getCache = body?.getCache ?? 1;
  const requestUrl = `/frontServer/active-page/list?isShop=1&getCache=${encodeURIComponent(getCache)}`;
  
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      requestUrl,
      {
        method: "POST",
        body: { ...body, unhaveToken: 1 },
      }
    );
  }
  return useNuxtApp().$customFetch(
    requestUrl,
    { method: "POST", body: { ...body, unhaveToken: 1 } }
  );
}
/**
 * 查询参数
 
 * @param params
 * @returns {*}
 */
export function getArgument(params) {
  return useNuxtApp().$customFetch(
    "/platform/portal/sysArgument/queryArgument",
    {
      method: "POST",
      body: { params },
    }
  );
}

/**
 * 查询配置
 
 * @param params
 * @returns {*}
 */
export function getWebsiteHomePageInfo(params) {
  const normalizedCurrentServer = normalizeMatchUrlValue(params.currentServer);
  const siteMatchEndpoint = getSiteMatchEndpointPath(
    useRuntimeConfig().public.siteMatchEndpoint,
  );
  traceSsr("matchUrl.request", {
    rawCurrentServer: params.currentServer,
    normalizedCurrentServer,
    siteMatchEndpoint,
  });

  return useNuxtApp().$customFetch(
    siteMatchEndpoint + "?getCache=1&currentServer=" +
      normalizedCurrentServer,
    {
      method: "POST",
      body: { url: normalizedCurrentServer }
     
    }
  ).then(async (response) => {
    traceSsr("matchUrl.response", {
      rawCurrentServer: params.currentServer,
      normalizedCurrentServer,
      success: response?.success,
      status: response?.status,
      msg: response?.msg || response?.message,
      siteInfoId: response?.data?.id || "",
      siteInfoTitle: response?.data?.title || "",
    });

    if (shouldTryDirectDevelopmentSiteInfo(response, normalizedCurrentServer)) {
      const directUrl =
        `${getUrlOrigin(normalizedCurrentServer)}/api/cmsadmin${siteMatchEndpoint}?currentServer=` +
        encodeURIComponent(normalizedCurrentServer);
      try {
        const directResponse = await $fetch(directUrl, {
          method: "POST",
          body: { url: normalizedCurrentServer },
        });
        traceSsr("matchUrl.directDevelopmentResponse", {
          directUrl,
          success: directResponse?.success,
          status: directResponse?.status,
          msg: directResponse?.msg || directResponse?.message,
          siteInfoId: directResponse?.data?.id || "",
          siteInfoTitle: directResponse?.data?.title || "",
        });
        if (directResponse?.success && directResponse?.data) {
          return directResponse;
        }
      } catch (error) {
        traceSsr("matchUrl.directDevelopmentError", {
          directUrl,
          message: error?.message || String(error),
        });
      }
    }

    return response;
  });
}

/**
 * 查询店铺配置
 
 * @param params
 * @returns {*}
 */
export function getWebStorePageInfo(params) {
  return new Promise((resolve) => {
    resolve({
      success: true,
      status: "SUCCESS",
      msg: "成功",
      data: {
        id: "919991359526363136",
        dealerInfoId: null,
        dealerUserId: null,
        title: "",
        logoUrl:
          "http://minio-tmall-test-intl.omszt.com/supplychain/rechargeProofFile/17396076409091726903639681%5E%E5%A4%A9%5E%E9%A9%AC%5E%E8%BF%90%5E%E9%80%9A%20%281%29%20%281%29%20%282%29%20%281%29.png?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=minioAdmin%2F20250911%2Fdefault%2Fs3%2Faws4_request&X-Amz-Date=20250911T023227Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=0ef63a016829013db6a482344e2252dc4566afcd8591f5107373b4b864fb6e3e",
        shopState: null,
        liveState: null,
        banLiveStartTime: null,
        banLiveEndTime: null,
        banLiveDuration: null,
        banLiveReason: null,
        deleted: null,
        followSum: 0,
        score: null,
        shopLiveState: 0,
        isFollow: 0,
        dealerShopLivePlanId: null,
        dealerItemSpuRespDTOS: null,
        shopIntroduction: null,
        shopBackUrl: null,
        shopScore: null,
        offiaccountFollowUrl:
          '{"customerService":"https://mp.weixin.qq.com/s?__biz=MzkyNjM5OTQ5Mw==&mid=2247483669&idx=1&sn=88e5ab68f6aabd130d9d265385f2c7a6&chksm=c236ac7ff5412569849877231d0a871e5c415b1be4b9070a5b49b496a100d93cf511504ec134#rd","welfareGroup":"","help":"https://mp.weixin.qq.com/s?__biz=MzkyNjM5OTQ5Mw==&mid=2247483670&idx=1&sn=7f20443769e7d1bb9c2128839771d9d2&chksm=c236ac7cf541256a32eab31a23cac9836484ef4974ddf73d83572c6967dde71219a829ee1358#rd","follow":"https://mp.weixin.qq.com/s?__biz=MzkyNjM5OTQ5Mw==&mid=2247483679&idx=1&sn=d713449910d1b75f8c5e9c4cd34464e9&chksm=c236ac75f541256363040c0f12309708951b9a419707c61f341ef8e10b0bb9e7ef63a1592a96#rd","newMemberGuide":"https://mp.weixin.qq.com/s?__biz=MzkyNjM5OTQ5Mw==&mid=2247483674&idx=1&sn=60429dece2c8cdcb95f552713c7c4208&chksm=c236ac70f54125662f651c1f09dee42c5234f44f93872b99814fefd51fc79df2ecfc1fa9787f#rd","prohibitedArticle":"https://mp.weixin.qq.com/s?__biz=MzkyNjM5OTQ5Mw==&mid=2247483668&idx=1&sn=b268f0ffc847af8ccd530662b842899b&chksm=c236ac7ef54125681bb48923ac711edb1bcb764e622c89b21d4c7021308800f1c84e0279fc14#rd","otherJson":"{\\n  \\"defaultCountry\\": \\"中國臺灣\\",\\n  \\"excutePhoneArea\\": [\\"86\\"],\\n  \\"defaultLocale\\": \\"zh-Hans\\",\\n  \\"mallApiUrl\\": \\"http://api-test.jyshop.omszt.com:8081/mall\\",\\n  \\"clientConfigUrl\\": \\"https://wcs0015.dzpaas.com:446/newwebchat/talking-box.html?pid=WCL-0065&sid=0000\\",\\n  \\"clientConfigs1\\": [\\n    {\\n      \\"type\\": \\"wechat\\",\\n      \\"icon\\": \\"https://sztjyshop-prod.oss-cn-guangzhou.aliyuncs.com/imgs/2023/01-16/upload_pic_1673856196040_%E5%BE%AE%E4%BF%A1.png\\",\\n      \\"url\\": \\"https://work.weixin.qq.com/kfid/kfcc8d8b84bffb3d116\\"\\n    },\\n    {\\n      \\"type\\": \\"line\\",\\n      \\"icon\\": \\"https://sztjyshop-prod.oss-cn-guangzhou.aliyuncs.com/imgs/2022/12-22/upload_pic_1671678199360_line%20(1).png\\",\\n      \\"url\\": \\"https://lin.ee/2BZVyxV\\"\\n    },\\n    {\\n      \\"type\\": \\"onlineService\\",\\n      \\"icon\\": \\"https://front-site.oss-cn-hongkong.aliyuncs.com/imgs/2024/09-26/footer-logo1.png\\",\\n      \\"url\\": \\"https://wcs0015.dzpaas.com:446/newwebchat/talking-box.html?pid=WCL-0065&sid=\\"\\n    }\\n  ],\\n  \\"aiUrl\\": \\"http://ai-test.m-shop-test.omszt.com:7888\\",\\n  \\"bindBuyMallUrl\\": \\"http://xjhy.mall-test.erp-buy-site.buyagent.omszt.com:8081\\",\\n  \\"hideProxyAliWithdrawal\\": \\"1\\",\\n  \\"aiHumanClientUrl\\": \\"https://wcs0015.dzpaas.com:446/newwebchat/talking-box.html?pid=WCL-0063&sid=\\",\\n  \\"dzClientChatUrl\\": \\"https://wcs0015.dzpaas.com:446/newwebchat/talking-box.html?pid=WCL-0063&sid=\\",\\n  \\"isHiddenCurrencySymbol\\": true,\\n  \\"appInfoPage\\": {\\n    \\"appLogo\\": \\"https://front-site.oss-accelerate.aliyuncs.com/imgs/2025/08-25/upload_pic_1756093371009_rainbow%20(2).png\\",\\n    \\"appName\\": \\"Rainbow Mall\\",\\n    \\"appInfoText\\": \\"Shop Everything From China With Guaranteed Service\\",\\n    \\"iosAppDownloadUrl\\": \\"https://apps.apple.com/cn/app/rainbow-mall/id1544424180\\",\\n    \\"androidAppDownloadUrl\\": \\"https://sztjyshop.oss-cn-guangzhou.aliyuncs.com/tmp/rainbowmall.apk\\",\\n    \\"appUrlScheme\\": \\"rainbowlogistics\\",\\n    \\"appBannerList\\": [\\n      {\\n        \\"picture\\": \\"https://front-site.oss-accelerate.aliyuncs.com/imgs/2025/08-25/upload_pic_1756093244089_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20250825114031_150.jpg\\"\\n      },\\n      {\\n        \\"picture\\": \\"https://front-site.oss-accelerate.aliyuncs.com/imgs/2025/08-25/upload_pic_1756093317326_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20250825114026_149.png\\"\\n      },\\n      {\\n        \\"picture\\": \\"https://front-site.oss-accelerate.aliyuncs.com/imgs/2025/08-25/upload_pic_1756093244089_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20250825114031_150.jpg\\"\\n      }\\n    ]\\n  }\\n}\\n","clientDownloadConfig":"[{\\"name\\":\\"最新版\\",\\"url\\":\\"https://nwjs.readthedocs.io/en/latest/For%20Users/Advanced/Use%20Native%20Node%20Modules\\"},{\\"name\\":\\"32位兼容版\\",\\"url\\":\\"https://baidu.com\\"}]"}',
        type: null,
        orgId: null,
        dealerShopLiveStreamerRespDTOList: null,
        ownerCode: null,
        ownerName: null,
        customerId: null,
        customerName: null,
        code: null,
        siteList: null,
        signUserId: null,
        contactName: null,
        contactPhone: null,
        contactTel: null,
        contactAddress: null,
        createTime: null,
        creatorCode: null,
        creatorName: null,
        modifyTime: null,
        modifierCode: null,
        modifierName: null,
        mineOwnerCode: null,
        mineOwnerName: null,
        memberCodeRule: null,
        maxim: "^最^快^、^最^稳^、^最^牛^逼",
        agreement: "<p>1^测^试^店^铺^专^用</p>",
        defaultLanguage: null,
        jyOrderRuleExpression: null,
        
        createOrderTips: null,
        defaultCurrencyName: null,
        autoAuditOrderFlag: null,
        batchAuditQuota: null,
        submitAllRelatedByInput: null,
        submitAllRelatedByImport: null,
        defaultCurrencySymbol: null,
        scId: "1",
        cancelOrderRule: null,
        openSmsVerify: 1,
        smsPlatform: null,
        partnership: null,
        freeDays: null,
        adventDays: null,
        beOverAutoDestroy: null,
        storageArticleDesc: null,
        noticeStrategy: null,
        showWarehouseWeightFlag: null,
        autoAuditRulesFlag: null,
        autoAuditRulesHandleTime: null,
        itemSum: 0,
      },
    });
  });

  return useNuxtApp().$customFetch("/admin/dealerShop/queryDealerShop", {
    method: "POST",
    body: { shopBackUrl: params.currentServer },
  });
}

/**
 * 查询站点身份
 * @param params
 * @returns {*}
 */
export function querySiteIdentityByShopId(params) {
  return useNuxtApp().$customFetch(
    `/admin/dealerShop/querySiteIdentityByShopId`,
    {
      method: "GET",
      params,
    }
  );
}

/**
 * 查询文章列表
 
 * @param params
 * @returns {*}
 */
export function getActiveListByTopicId($axios, params) {
  return useNuxtApp().$customFetch(
    "/admin/manage/article/queryArticleListByTopicId",
    {
      method: "POST",
      body: { params },
    }
  );
}

// 获取Tabbar配置
export function getTabBarConfigList(params) {
  return useNuxtApp().$customFetch(
    "/frontServer/tabBar/list",
    {
      method: "POST",
      body: params,
    }
  );
}

// 获取商城商品菜单（分类/品牌展示入口）
export function getMallGoodsMenuList(params, $fetch, headers) {
  const body = normalizeMallGoodsRequestBody(params, headers);
  return postMallGoods(MALL_GOODS_MENU_URL, body, $fetch, headers);
}
// 获取文章详情
export function getArticleDetails(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/manage/article/articleList",
    {
      method: "POST",
      body: params,
    }
  );
}
/**
 * 查询文章列表
 
 * @param params
 * @returns {*}
 */
export function getActiveListFormPlatform(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/platform/admin/manage/article/articleList",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/platform/admin/manage/article/articleList",
      {
        method: "POST",
        body: params,
      }
    );
  }
}

/**
 * 查询tags列表
 
 * @param params
 * @returns {*}
 */
export function getTagsFormPlatform(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/tagDetail/tagDetailPageList",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 批量查询文章列表
 
 * @param params
 * @returns {*}
 */
export function mutiQueryArticleInCache($fetch, headers, params) {
  return makeUnSetupFetch(
    $fetch,
    headers,
    "/platform/admin/manage/article/mutiQueryArticleInCache",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 查询文章分类
 
 * @param params
 * @returns {*}
 */
export function getArticleCategoryList(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/manage/articleCategory/articleCategoryList",
    { method: "POST", body: params }
  );
}

/**
 * 查询商品列表
 
 * @param params
 * @returns {*}
 */
export function getGoodsListFormPlatform($axios, url, params) {
  return useNuxtApp().$customFetch(url, params, {
    method: "POST",
    body: params,
    changeOrigin: true,
    headers: setHeaders,
  });
}

/**
 * 查询1688商品列表
 
 * @param params
 * @returns {*}
 */
export function get1688GoodsList(params, $fetch, headers) {

  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchKeywordQuery",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchKeywordQuery",
      {
        method: "POST",
        body: params,
      }
    );
  }
}

/**
 * 查询独立站商品列表
 
 * @param params
 * @returns {*}
 */
export function getShopGoodsList(params, $fetch, headers) {
  const body = normalizeMallGoodsRequestBody(params, headers);
  const requestUrl = shouldUseMixedGoodsList(headers)
    ? MALL_MIXED_GOODS_LIST_URL
    : MALL_GOODS_LIST_URL;
  return postMallGoods(requestUrl, body, $fetch, headers);
}

/**
 * 查询独立站普通商品和组合商品混合列表。
 * 低代码明确选择 shopMixed，或模板开启 supportsComboProducts 能力时调用。
 */
export function getShopMixedGoodsList(params, $fetch, headers) {
  const body = normalizeMallGoodsRequestBody(params, headers);
  return postMallGoods(MALL_MIXED_GOODS_LIST_URL, body, $fetch, headers);
}



/**
 * 查询独立站商品列表
 
 * @param params
 * @returns {*}
 */
export function getShopGoodsFilterList(params, $fetch, headers) {
  return getShopGoodsList(params, $fetch, headers);
}

/**
 * 查询日本首页广告图配置
 *
 * @param params
 * @returns {*}
 */
export function getJapanHomeAdPage(params, $fetch, headers) {
  const body = params && params.param
    ? { ...params, param: { ...params.param } }
    : { param: { ...(params || {}) } };

  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/ad/queryPage",
      {
        method: "POST",
        body,
      }
    );
  }

  return useNuxtApp().$customFetch("/mall/admin/ad/queryPage", {
    method: "POST",
    body,
  });
}



/**
 * 查询独立站商品列表根据品牌的分类
 
 * @param params
 * @returns {*}
 */
export function searchByBrandAndCategory(params, $fetch, headers) {
  return getShopGoodsList(params, $fetch, headers);
}
/**
 * 查询独立站商品详情
 * @param params
 * @returns {*}
 */
export function getProductDetail(params, $fetch, headers) {  
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/mallItemComment/getItemCommentBySpuId?isShop=1",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/mallItemComment/getItemCommentBySpuId?isShop=1",
      {
        method: "POST",
        body: params,
      }
    );
  }
}
export function getGoodDetail(params, $fetch, headers) {  
  // ensure dealerShopId exists in body.param from cookie on both SSR and client
  const body = { ...(params || {}) };
  const innerParam = { ...(body.param || {}) };

  if (!innerParam.dealerShopId) {
    try {
      const dealerShopIdCookie = useCookie("dealerShopId", { path: "/" });
      if (dealerShopIdCookie && dealerShopIdCookie.value) {
        innerParam.dealerShopId = dealerShopIdCookie.value;
      }
    } catch (e) {
      // SSR fallback: parse from incoming request headers
      if (headers && headers.cookie && typeof headers.cookie === "string") {
        const match = headers.cookie.match(/(?:^|;\s*)dealerShopId=([^;]+)/);
        if (match && match[1]) {
          try {
            innerParam.dealerShopId = decodeURIComponent(match[1]);
          } catch (_) {}
        }
      }
    }
  }

  body.param = innerParam;

  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/mallGoods/itemSpuDetail?isShop=1",
      {
        method: "POST",
        body,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/mallGoods/itemSpuDetail?isShop=1",
      {
        method: "POST",
        body,
      }
    );
  }
}


/**
 * 查询1688店铺商品列表
 
 * @param params
 * @returns {*}
 */
export function get1688SellerGoodsList(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchQuerySellerOfferList",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchQuerySellerOfferList",
      {
        method: "POST",
        body: params,
      }
    );
  }
}

/**
 * 查询1688商品列表从图片
 
 * @param params
 * @returns {*}
 */
export function get1688GoodsListByImg(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchImageQuery",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchImageQuery",
      {
        method: "POST",
        body: params,
      }
    );
  }
}

/**
 * 查询1688 图片
 * @param params
 * @returns {*}
 */
export function upload1688Img(params, $fetch, headers) {
  return useNuxtApp().$customFetch(
    "/mall/admin/alibabaGoods/productImageUpload",
    {
      method: "POST",
      body: params,
    }
  );
}




/**
 * 1688商品详情
 * @param params
 * @returns {*}
 */
export function get1688ProductDetail(params) {
  return useNuxtApp().$customFetch(
    "/mall/admin/alibabaGoods/productSearchQueryProductDetail?mall=1",
    {
      method: "POST",
      body: params,
      headers: {
        language: params.param.country,
        transferKeysConfig: JSON.stringify([
          ["data.sellerLoginId"],
          ["data.productShippingInfo.sendGoodsAddressText"],
        ]),
      },
    }
  );
}

/**
 * 加入购物车
 */
export function tbGlobalAddItemsToCart(params) {
  const nuxtApp = useNuxtApp();
  const requestParam = params?.param || params || {};
  const addCartVisitOptions = {
    nuxtApp,
    shopId: resolveOnlineVisitShopId({
      nuxtApp,
      shopId: requestParam.dealerShopId || requestParam.shopId,
    }),
  };

  return nuxtApp.$customFetch("/mall/admin/mallShoppingCart/add?mall=1", {
    method: "POST",
    body: params,
  }).then((response) => {
    if (response?.success) {
      void reportAddCartVisit(addCartVisitOptions);
    }

    return response;
  });
}

/**
 * 查询仓库列表
 * @param params
 * @returns {*}
 */
export function getWarehouseList(params) {
  return useNuxtApp().$customFetch(
    `/platform/admin/dealer-shop-warehouse/query/warehouseList`,
    {
      method: "GET",
      params,
    }
  );
}


 
